import * as React from 'react';
import * as _ from 'underscore';

import * as CertificationWatermark from '@tableau/certification-watermark';
import { ActionCellWrapper, ActionCellWrapperParams } from '@tableau/data-catalog-client-components';
import { BodyCell, DataGrid } from '@tableau/data-grid-3-wrapper';
import { SiteUrls } from '@tableau/shared-urls';
import { TabStyles } from '@tableau/widgets';

import { Formatters, Messages } from '../../../codegen/Localize';
import { CommonStyles } from '../../../common/react/CommonStyles';
import { ProfilePicture } from '../../../common/react/components/ProfilePicture';
import { IReactTranslateService } from '../../../common/react/IReactTranslateService';
import { getFlowOutputStepRunIconClass } from '../../../flow/utils/FlowIconUtils';
import { localizeFlowRunHistoryStatus } from '../../../flow/utils/FlowStatusUtils';
import { serverScheduleUrl, siteScheduleUrl } from '../../../schedule/utils/ScheduleUtils';
import { ISiteSettingsForServerAdmin } from '../../../settings/types/ISite';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { SiteRolesHelper } from '../../../ts/helpers/SiteRolesHelper';
import { TextileHelper } from '../../../ts/helpers/TextileHelper';
import { L10n } from '../../../ts/L10n/L10n';
import { GraphDatabase, GraphTable, mapGraphTypeToContentInfoForActions } from '../../../ts/mappers/DatabaseAndTableMapper';
import { MapperHelper } from '../../../ts/mappers/MapperHelper';
import { UserMapper } from '../../../ts/mappers/UserMapper';
import { ContentSpaceDefinitions, CustomViewDefinition, DataConnectionDefinition, DatasourceDefinition, ExplorableItemDefinitions, FlowDefinition, ViewDefinition, WorkbookDefinition } from '../../../ts/models/ContentDefinitions';
import { MetricDefinition } from '../../../metrics/models/MetricDefinition';
import { ExplorableItemType, getKeyForExplorableItem, isSpaceInfoItemType } from '../../../ts/models/ContentType';
import { DeviceType } from '../../../ts/models/DeviceType';
import { FlowBaseInfo } from '../../../ts/models/FlowInfo';
import { FlowOutputStepRunListItemInfo } from '../../../ts/models/FlowOutputStepRunInfo';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { IDataAlertInfo } from '../../../ts/models/IDataAlertInfo';
import { IDataConnectionInfo } from '../../../ts/models/IDataConnectionInfo';
import { IDataSourceInfo } from '../../../ts/models/IDataSourceInfo';
import { IGroupInfo } from '../../../ts/models/IGroupInfo';
import { IConnectedViewInfo } from '../../../metrics/models/IMetricInfo';
import { IScheduleInfo } from '../../../ts/models/IScheduleInfo';
import { IServerUserInfo } from '../../../ts/models/IServerUserInfo';
import { ISharedWithMeContentInfo } from '../../../ts/models/ISharedWithMeContentInfo';
import { ISiteUserInfo } from '../../../ts/models/ISiteUserInfo';
import { ISubscriptionInfo } from '../../../ts/models/ISubscriptionInfo';
import { IWorkbookInfo } from '../../../ts/models/IWorkbookInfo';
import { OrderKeys } from '../../../ts/models/OrderKeys';
import {
  IExplorableItemTypeInfo,
  IFavoritableItemInfo,
  IItemStatisticsInfo,
  INamedItemInfo
} from '../../../ts/models/resourceInfoParts';
import { SortOptions } from '../../../ts/models/sort/CommonSortOptions';
import { IBackgroundJobInfo } from '../../../ts/resources/BackgroundJobs';
import { DataConnections } from '../../../ts/resources/DataConnections';
import { DataSources } from '../../../ts/resources/Datasources';
import { Flows } from '../../../ts/resources/Flows';
import { ExtractEncryptionMode } from '../../../ts/resources/Sites';
import { RemoteRefreshTypes } from '../../../ts/ServerApi/requests/GetRemoteRefreshSchedulesRequest';
import { IShareAction } from '../../../ts/ServerApi/requests/GetSharedWithMeRequest';
import { getBackgroundJobStatusTooltip, getIconForBackgroundJob, IBackgroundJob, localizeBackgroundJobState, localizeBackgroundJobTaskType } from '../../../ts/ServerApi/types/IBackgroundJob';
import { ExtractTaskType } from '../../../ts/ServerApi/types/IExtractTask';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { ISiteNameWithId } from '../../../ts/ServerApi/types/ISiteNameWithId';
import { SiteRoles } from '../../../ts/ServerApi/types/ISiteRole';
import { ManageDataAlertRecipientsDialog } from '../../../ts/services/ManageDataAlertRecipientsDialog';
import { ServerService } from '../../../ts/services/ServerService';
import { SubscriptionGrid } from '../../content/SubscriptionGrid';
import { ItemsSet } from '../../ItemsSet';
import { ServerUrls } from '../../ServerUrls';
import { isAllCollectionsSpace, isDatasourceConnectionSubSpace, isRecentsSpace, isScheduleSpace, isSiteTasksSpace, isSiteUserSpace, isWorkbookSpace, SpaceInfo, isSharedWithMeSpace } from '../../state/SpaceInfo';
import { BackgroundJobExtendedInfoDialogCell } from '../BackgroundJobsSpace/BackgroundJobExtendedInfoDialogCell';
import { CollectionVisibility } from '../Collections/CollectionModels';
import { getCommonDatasourceInfo } from '../CommonDatasourceInfo';
import { GroupDataGridCells } from '../groups/GroupDataGridCells';
import { SiteGridCells } from '../ServerSpaces/Sites/SiteGridCells';
import { UserDataGridCells } from '../SiteUsersSpace/SiteUserDataGridCells';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { GreyedOutTextStyle, HiddenUserStyles } from '../SpacesStyles';
import { EncryptionTooltip } from '../Tooltips/EncryptionTooltip';
import { SchedulesGridCells } from './../Schedules/SchedulesGridCells';
import { DataAlertDataGridCells } from './DataAlertGridCells';
import { CertifiableConnectsToCell, ConnectionHoverPanel, ConnectsToCell } from './DataConnectionComponents';
import { DataConnectionDataGridCells } from './DataConnectionDataGridCells';
import { DataGridCells } from './DataGridCells';
import { SelectableSortKeyColumnContext, SelectableSortKeyColumnItem, SelectableSortKeyColumnOptions, SelectableSortKeyDataGridColumn } from './SelectableSortKeyDataGridColumn';
import { SortableHeader } from './SortableHeader';
import { AlertCellHeader, CenterAlignedCell, DataQualityWarningCellHeader, RightAlignedCell, RightAlignedHeader, StyledCellFormatter } from './StyledCellWrapper';
import { CommonColumn } from './types/Columns';
import { HelpService } from '../../../commonInfra/services/HelpService';
import { ContentDraftStatus } from '../../../ts/ServerApi/types/ContentDraft';
import { ISchedule } from '../../../ts/ServerApi/types/ISchedule';
import { IAccelerationTaskInfo } from '../../../ts/models/IAccelerationTaskInfo';
import { IExtractTaskInfo } from '../../../ts/models/IExtractTaskInfo';

export const IconColumnPreferredWidth = 44;

export type ColumnFactory = (params: ListViewGridColumnGeneratorParams & ActionCellWrapperParams<DataGrid.Column>) => DataGrid.Column;

/**
 * Columns can be defined in either of two ways:
 * - As a value of the CommonColumn enum with a corresponding column definition in this file's switch statement
 * - As a factory function that takes in a subset of the params and returns a DataGrid.Column object
 */
export type ColumnType = CommonColumn | ColumnFactory;

export interface ListViewGridColumnGeneratorParams {
  readonly addContentFavorite: (item: IFavoritableItemInfo) => any;
  readonly removeContentFavorite: (item: IFavoritableItemInfo) => any;
  readonly selectedItems: ItemsSet<IExplorableItemTypeInfo>;
  readonly selectItem: (item: IContentInfo) => void;
  readonly deselectItem: (item: IContentInfo) => void;
  readonly angularContext: AngularContext;
  readonly urls: SpacesSiteUrls;
  readonly contentFilter: ReadonlyArray<ExplorableItemType>;
  readonly showingMixedContent: boolean;
  readonly currentSort: ReadonlyArray<IOrderClause>;
  readonly changeSort: (field: string) => void;
  readonly setSortOrder: (order: ReadonlyArray<IOrderClause>) => void;
  readonly displayingItems: boolean;
  readonly isSessionSiteOrServerAdmin: boolean;
  readonly spaceInfo: SpaceInfo;
  readonly displaySchedulesInClientTimezone: boolean;
  readonly displayScheduleDescriptionAsName: boolean;
  readonly inServerArea: boolean;
  readonly flowFailureThresholdForRunPrevention: number;
}

export class ListViewGridColumnGenerator {
  private params: ListViewGridColumnGeneratorParams;

  private sanitize: ng.sanitize.ISanitizeService;
  private dataConnections: DataConnections;
  private server: ServerService;
  private translate: ng.translate.ITranslateService;
  private manageDataAlertRecipientsDialog: ManageDataAlertRecipientsDialog;

  public constructor(params: ListViewGridColumnGeneratorParams) {
    this.params = params;

    this.sanitize = this.params.angularContext.getService('$sanitize');
    this.dataConnections = this.params.angularContext.getService('DataConnections');
    this.server = this.params.angularContext.getService('ServerService');
    this.translate = this.params.angularContext.getService('$translate');
    this.manageDataAlertRecipientsDialog = this.params.angularContext.getService('ManageDataAlertRecipientsDialog');
  }

  public updateParams(paramOverrides: Partial<ListViewGridColumnGeneratorParams>) {
    this.params = { ...this.params, ...paramOverrides };
  }

  // Warning: where possible, the actions menu should be in its own column in the data grid
  // to follow aria best practices for grid keyboard navigation. To make separate,
  // add CommonColumn.ActionsMenu to list of column types where desired in ListViewGridContentGenerator
  private graphActionCellWrapper: ActionCellWrapper<DataGrid.Column> = (originalColumn: DataGrid.Column, styleOverrides: React.CSSProperties) => {
    const actionsOptions: DataGridCells.ActionCellOptions = {
      ...this.generateActionsOptions(),
      getContentItem: (row: DataGrid.Row) => mapGraphTypeToContentInfoForActions(row.data as GraphDatabase | GraphTable),
    };

    const ActionsCellStyleOverrides: React.CSSProperties = {
      paddingLeft: 0,         // Don't double up on paddingLeft for the originalColumn
      paddingRight: 0,        // To put the actions menu up against the right edge of the column
      overflow: 'visible'     // To let the action menu extend beyond the row
    };

    const actionCellFormatter = DataGridCells.ActionsCell(actionsOptions, originalColumn.cellFormatter);

    return {
      ...originalColumn,
      cellFormatter: StyledCellFormatter(actionCellFormatter, { ...ActionsCellStyleOverrides, ...styleOverrides})
    };
  }

  public getColumn(columnDefinition: ColumnType): DataGrid.Column | null {
    if (columnDefinition instanceof Function) {
      return columnDefinition({ ...this.params, actionCellWrapper: this.graphActionCellWrapper });
    } else {
      return this.columnFromEnum(columnDefinition);
    }
  }

  private columnFromEnum(columnDefinition: CommonColumn): DataGrid.Column | null {
    const sortableHeaderFormatter = SortableHeader.Formatter(this.params.currentSort, this.params.changeSort);
    const showEncryptionTooltip: boolean = this.server.isExtractEncryptionEnabledOmniSwitch() && this.server.getExtractEncryptionModeForSite() !== ExtractEncryptionMode.DISABLED;
    const subscriptionOnExtractRefreshFn = (row: DataGrid.Row) => {
      const subscription = row.data as ISubscriptionInfo;
      return FeatureFlags.subscriptionsOnExtractRefreshEnabled && subscription.refreshExtractTriggered;
    };

    switch (columnDefinition) {
      case CommonColumn.Select:
        return {
          name: CommonColumn.Select,
          dataKey: 'selected',
          headerText: '',
          resizable: false,
          sortable: false,
          // Account for first column's left margin
          preferredWidth: this.params.displayingItems ? IconColumnPreferredWidth + TabStyles.Spacing.DefaultSpace : 0,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.CheckboxCell({
            onCheckboxClicked: this.onSelectedCheckboxClicked,
            isRowSelected: this.isRowSelected,
            checkboxCellTestIdFn: this.checkboxCellTestId
          })
        };

      case CommonColumn.Favorite:
        return {
          name: CommonColumn.Favorite,
          dataKey: 'favorite',
          headerText: '',
          resizable: false,
          sortable: false,
          preferredWidth: this.params.displayingItems ? IconColumnPreferredWidth : 0,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.FavoriteCell({
            onAddFavorite: this.onAddFavorite,
            onRemoveFavorite: this.onRemoveFavorite,
            isFavoritable: this.isFavoritable,
            favoriteCellTestIdFn: this.favoriteCellTestId,
            isAccessDenied: this.isAccessDenied,
          })
        };

      case CommonColumn.DataQualityIndicator:
        const siteUrl = new SiteUrls({
          siteUrlName: this.server.sessionSiteUrlName()
        });

        if (!this.server.catalogingOnSiteEnabled()) {
          return null;
        }

        return {
          name: CommonColumn.DataQualityIndicator,
          dataKey: 'dataqualityindicator',
          headerText: '',
          resizable: false,
          sortable: false,
          preferredWidth: this.params.displayingItems ? IconColumnPreferredWidth : 0,
          headerTooltip: Messages.column_has_data_quality_warning(),
          headerFormatter: DataQualityWarningCellHeader(sortableHeaderFormatter),
          cellFormatter: DataGridCells.DataQualityIndicatorCell(siteUrl, this.hasDataQualityIndicator)
        };

      case CommonColumn.ContentType:
        return {
          name: CommonColumn.ContentType,
          dataKey: OrderKeys.contentTypeOrder,
          headerText: Messages.column_content_type(),
          resizable: false,
          sortable: this.params.showingMixedContent
            && !isRecentsSpace(this.params.spaceInfo)
            && !isSharedWithMeSpace(this.params.spaceInfo),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.ContentTypeCell({
            imageUrlForRow: this.contentTypeImageUrlForRow,
            titleForIcon: this.contentTypeTitleForIconForRow,
            isCertified: this.isCertifiedDatasource,
            getTooltipProps: this.generateDSCertificationTooltipProps
          })
        };

      case CommonColumn.Name:
        let nameColumn: DataGrid.Column = {
          name: CommonColumn.Name,
          dataKey: OrderKeys.name,
          sortable: true,
          headerText: Messages.column_name(),
          direction: 'asc',
          growToFit: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.LinkCell(this.generateLinkForName)
        };
        return nameColumn;

      case CommonColumn.NameWithMarkers:
        let nameColumnWithMarkers: DataGrid.Column = {
          name: CommonColumn.NameWithMarkers,
          dataKey: OrderKeys.name,
          sortable: true,
          headerText: Messages.column_name(),
          direction: 'asc',
          growToFit: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.LinkWithDraftStatus(this.generateLinkForNameWithStatus)
        };
        return nameColumnWithMarkers;

      case CommonColumn.NameWithAccessStatus:
        return {
          name: CommonColumn.NameWithAccessStatus,
          dataKey: OrderKeys.name,
          sortable: !isSharedWithMeSpace(this.params.spaceInfo),
          headerText: Messages.column_name(),
          direction: 'asc',
          growToFit: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.LinkCellWithAccessStatus(this.generateLinkForName)
        };

      case CommonColumn.ActionsMenu:
        let actionsMenuCol: DataGrid.Column = {
          name: CommonColumn.ActionsMenu,
          dataKey: 'actions',
          headerText: Messages.actions(),
          sortable: false,
          resizable: false,
          cellFormatter: DataGridCells.ActionsOnlyCell(this.generateActionsOptions())
        };
        return actionsMenuCol;

      case CommonColumn.ProjectsCount:
        return {
          name: CommonColumn.ProjectsCount,
          dataKey: OrderKeys.childProjectCount,
          sortable: true,
          direction: 'desc',
          headerText: Messages.column_numChildren(),
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('childProjectCount'))
        };

      case CommonColumn.WorkbooksCount:
        return {
          name: CommonColumn.WorkbooksCount,
          dataKey: OrderKeys.workbookCount,
          sortable: true,
          direction: 'desc',
          headerText: Messages.column_numWorkbooks(),
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('workbookCount'))
        };

      case CommonColumn.DatasourceInWorkbooksCount:
        return {
          name: CommonColumn.DatasourceInWorkbooksCount,
          dataKey: OrderKeys.connectedWorkbooksCount,
          sortable: true,
          direction: 'desc',
          headerText: Messages.column_numWorkbooks(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.LocalizedCountWithBar(
            (data: IDataSourceInfo) => data.connectedWorkbooksCount,
            (data: IDataSourceInfo) => data.usageInfoMaxValues && data.usageInfoMaxValues['connectedWorkbooksCount']
          )
        };

      case CommonColumn.Hits:
        const items: SelectableSortKeyColumnItem[] = _.map(SortOptions.hits.subSortOptions, sortOption => {
          return {
            dataKey: sortOption.field,
            headerText: sortOption.getButtonDisplayValue(),
            order: sortOption.toOrderClauseArray(),
            cellFormatter: DataGridCells.LocalizedCountWithBar(
              (data: IItemStatisticsInfo<any>) => data.usageInfo && data.usageInfo[sortOption.field],
              (data: IItemStatisticsInfo<any>) => data.usageInfoMaxValues && data.usageInfoMaxValues[sortOption.field])
          };
        });

        let columnContext: SelectableSortKeyColumnContext = {
          currentSort: this.params.currentSort,
          changeSort: this.params.changeSort,
          setSortOrder: this.params.setSortOrder
        };
        let columnOptions: SelectableSortKeyColumnOptions = {
          columnName: CommonColumn.Hits,
          defaultDataKey: OrderKeys.hitsTotal,
          items: items
        };
        return SelectableSortKeyDataGridColumn(columnOptions, columnContext, true /* sortable */);

      case CommonColumn.ViewsCount:
        return {
          name: CommonColumn.ViewsCount,
          dataKey: OrderKeys.viewCount,
          sortable: true,
          direction: 'desc',
          headerText: Messages.column_numViews(),
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('viewCount'))
        };

      case CommonColumn.SheetsCount:
        return {
          name: CommonColumn.SheetsCount,
          dataKey: OrderKeys.numSheets,
          sortable: true,
          direction: 'desc',
          headerText: Messages.column_numSheets(),
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('numSheets'))
        } as any;

      case CommonColumn.Workbook:
        return {
          name: CommonColumn.Workbook,
          dataKey: OrderKeys.workbookName,
          sortable: true,
          direction: 'asc',
          headerText: Messages.column_workbook(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.LinkCell(this.generateLinkForWorkbook)
        };

      case CommonColumn.DatasourcesCount:
        return {
          name: CommonColumn.DatasourcesCount,
          dataKey: OrderKeys.datasourceCount,
          sortable: true,
          direction: 'desc',
          headerText: Messages.column_numDatasources(),
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('datasourceCount'))
        };

      case CommonColumn.Size:
        return {
          name: CommonColumn.Size,
          dataKey: OrderKeys.size,
          sortable: true,
          headerText: Messages.column_size(),
          direction: 'desc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(DataGridCells.SizeCell('size'))
        };

      case CommonColumn.SheetNumber:
        return {
          name: CommonColumn.SheetNumber,
          dataKey: OrderKeys.index,
          sortable: true,
          direction: 'asc',
          headerText: Messages.column_sheetNumber(),
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('index'))
        };

      case CommonColumn.ConnectsTo:
        const column = {
          name: CommonColumn.ConnectsTo,
          dataKey: 'connectsTo',
          headerText: Messages.column_connects_to(),
          sortable: false,
          growToFit: this.showingOnlyDataConnections(),
          headerFormatter: sortableHeaderFormatter
        };

        const getPopoverContent = ConnectionHoverPanel.getPopoverContent(this.dataConnections, this.params.urls);
        let cellFormatter = ConnectsToCell.cellFormatter(this.params.urls, getPopoverContent, ConnectionHoverPanel.shouldDisplayPanel);
        if (FeatureFlags.datasourceCertificationEnabled && isWorkbookSpace(this.params.spaceInfo)) {
          cellFormatter = CertifiableConnectsToCell.cellFormatter(
            this.isCertifiedDatasource, this.generateDSCertificationTooltipProps, this.params.urls,
            getPopoverContent, ConnectionHoverPanel.shouldDisplayPanel);
        }

        if (this.showingOnlyDataConnections()) {
          // We can't use the StyledCellFormatter because it makes it so you have to click twice for a link to work
          // so instead we add the left padding here
          const leftPaddingStyle = { paddingLeft: CommonStyles.Sizes.MediumSpace };
          const connectsToColumn = { ...column, cellFormatter: StyledCellFormatter(cellFormatter, leftPaddingStyle) };
          return connectsToColumn;
        } else {
          const connectsTocolumn = { ...column, cellFormatter: StyledCellFormatter(cellFormatter) };
          return connectsTocolumn;
        }

      case CommonColumn.Project:
        return {
          name: CommonColumn.Project,
          dataKey: OrderKeys.projectName,
          sortable: true,
          headerText: Messages.column_project(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.LinkCell(this.generateLinkForProject)
        };

      case CommonColumn.Owner:
        return {
          name: CommonColumn.Owner,
          dataKey: OrderKeys.ownerName,
          sortable: !isAllCollectionsSpace(this.params.spaceInfo)
            && !isSharedWithMeSpace(this.params.spaceInfo),
          headerText: Messages.column_owner(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.LinkCell(this.generateLinkForOwner)
        };

      case CommonColumn.OwnerWithIcon:
        return {
          name: CommonColumn.OwnerWithIcon,
          dataKey: OrderKeys.ownerName,
          sortable: !isAllCollectionsSpace(this.params.spaceInfo),
          headerText: Messages.column_owner(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.LinkCell(this.generateLinkForOwnerWithIcon)
        };

      case CommonColumn.Created:
        return {
          name: CommonColumn.Created,
          dataKey: OrderKeys.createdAt,
          sortable: true,
          headerText: Messages.column_created(),
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.DateTimeCell('createdAt')
        };

      case CommonColumn.LastAccessedAt:
        return {
          name: CommonColumn.LastAccessedAt,
          dataKey: OrderKeys.lastAccessedAt,
          sortable: true,
          headerText: Messages.column_lastAccessedAt(),
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.DateTimeCell('lastAccessedAt')
        };

      case CommonColumn.Modified:
        return {
          name: CommonColumn.Modified,
          dataKey: OrderKeys.updatedAt,
          sortable: true,
          headerText: Messages.column_updated(),
          direction: 'desc',
          resizable: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.DateTimeCell('updatedAt')
        };

      case CommonColumn.ModifiedBy:
        return {
          name: CommonColumn.ModifiedBy,
          dataKey: OrderKeys.modifiedBy,
          sortable: true,
          headerText: Messages.column_modified_by(),
          direction: 'asc',
          resizable: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.LinkCell(this.generateLinkForModifiedBy)
        };

      case CommonColumn.ExtractStatusOnly:
        return {
          name: CommonColumn.ExtractStatusOnly,
          dataKey: OrderKeys.lastRefreshedAt,
          headerText: FeatureFlags.extractMoveToTheWebEnabled ? Messages.extractStatusLabel() : Messages.column_last_refresh(),
          sortable: true,
          resizable: true,
          direction: 'asc',
          headerFormatter: SortableHeader.Formatter(this.params.currentSort, this.params.changeSort, showEncryptionTooltip ? <EncryptionTooltip /> : null),
          cellFormatter: DataGridCells.LastRefreshedCell(this.server.isExtractEncryptionEnabledOmniSwitch(), this.server.getExtractEncryptionModeForSite())
        };

      case CommonColumn.ExtractStatus:
        {
          const dateColumnSpec: SelectableSortKeyColumnOptions = {
            columnName: CommonColumn.ExtractStatus,
            defaultDataKey: OrderKeys.lastRefreshedAt,
            items: [
              {
                dataKey: OrderKeys.lastRefreshedAt,
                headerText: FeatureFlags.extractMoveToTheWebEnabled ? Messages.extractStatusLabel() : Messages.column_last_refresh(),
                order: [{ field: OrderKeys.lastRefreshedAt, ascending: false }],
                cellFormatter: DataGridCells.LastRefreshedCell(this.server.isExtractEncryptionEnabledOmniSwitch(), this.server.getExtractEncryptionModeForSite()),
                tooltip: showEncryptionTooltip ? EncryptionTooltip : null
              },
              {
                dataKey: OrderKeys.updatedAt,
                headerText: Messages.column_updated(),
                order: [{ field: OrderKeys.updatedAt, ascending: false }],
                cellFormatter: DataGridCells.DateTimeCell('updatedAt')
              }
            ]
          };

          const extractColumnContext: SelectableSortKeyColumnContext = {
            currentSort: this.params.currentSort,
            changeSort: this.params.changeSort,
            setSortOrder: this.params.setSortOrder
          };
          return SelectableSortKeyDataGridColumn(dateColumnSpec, extractColumnContext, true /*sortable*/);
        }

      case CommonColumn.Alert:
        return {
          name: CommonColumn.Alert,
          dataKey: OrderKeys.hasAlert,
          sortable: true,
          headerText: '',
          direction: 'desc',
          resizable: false,
          headerTooltip: Messages.column_has_alert(),
          headerFormatter: AlertCellHeader(sortableHeaderFormatter),
          cellFormatter: DataGridCells.AlertCell(this.generateAlert)
        };

      case CommonColumn.ConnectionType:
        return {
          name: CommonColumn.ConnectionType,
          dataKey: OrderKeys.connectionTypeDisplayName,
          sortable: true,
          direction: 'asc',
          headerText: Messages.column_connection_type(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.PlainTextCell(OrderKeys.connectionTypeDisplayName)
        };

      case CommonColumn.Authentication:
        return {
          name: CommonColumn.Authentication,
          dataKey: 'authentication',
          sortable: false,
          headerText: Messages.authentication(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataConnectionDataGridCells.AuthenticationCellFormatter()
        };

      case CommonColumn.Username:
        return {
          name: CommonColumn.Username,
          dataKey: 'username',
          sortable: false,
          headerText: Messages.username(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataConnectionDataGridCells.UsernameCellFormatter()
        };

      case CommonColumn.Datasource:
        return {
          name: CommonColumn.Datasource,
          dataKey: OrderKeys.name,
          sortable: true,
          headerText: Messages.column_datasource(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.LinkCell(this.generateLinkForDataconnectionDatasource)
        };

      case CommonColumn.Subject:
        return {
          name: CommonColumn.Subject,
          dataKey: OrderKeys.subject,
          growToFit: isScheduleSpace(this.params.spaceInfo),
          sortable: true,
          headerText: Messages.column_task_subject(),
          headerFormatter: sortableHeaderFormatter,
          direction: 'asc',
          cellFormatter: DataGridCells.PlainTextCell(OrderKeys.subject),
        };

      case CommonColumn.UsernameForSubscription:
        const userNameColumn = {
          name: CommonColumn.UsernameForSubscription,
          dataKey: OrderKeys.userDisplayName,
          sortable: true,
          headerText: Messages.column_task_user(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.LinkCell((row: DataGrid.Row) => {
            const subscription = row.data as ISubscriptionInfo;
            return {
              text: subscription.user.displayName,
              url: new SpacesSiteUrls({ siteUrlName: subscription.site.urlName })
                .userUrl(subscription.user.username, subscription.user.domainName),
              iconClassName: 'tb-grid-icon-user',
              iconTitle: Messages.user()
            };
          })
        };

        return userNameColumn;

      case CommonColumn.SubscribedTo:
        const subscribedToColumn = {
          name: CommonColumn.SubscribedTo,
          dataKey: OrderKeys.targetName,
          sortable: true,
          headerText: Messages.column_task_subscription(),
          direction: 'asc',
          growToFit: isSiteUserSpace(this.params.spaceInfo),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.LinkCell((row: DataGrid.Row) => {
            const subscription = row.data as ISubscriptionInfo;
            return SubscriptionGrid.getTargetLinkMetadata(subscription);
          })
        };
        return subscribedToColumn;

      case CommonColumn.Schedule:
        return {
          name: CommonColumn.Schedule,
          dataKey: OrderKeys.scheduleName,
          sortable: true,
          direction: 'asc',
          headerText: Messages.column_task_schedule(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.ScheduleDescriptionCellWithIcon(
            (row: DataGrid.Row) => {
              const subscription = row.data as ISubscriptionInfo;
              return subscription.canSpecifyDataCondition && subscription.dataConditionPresent;
            },
            this.translate.instant.bind(this.translate),
            this.params.isSessionSiteOrServerAdmin ?
              (row: DataGrid.Row) => {
                const subscription = row.data as ISubscriptionInfo;
                const schedule = subscription.schedule;
                //TODO: MAKE THIS WORK FOR THE SERVER PAGE
                return siteScheduleUrl(new SpacesSiteUrls({ siteUrlName: subscription.site.urlName }), schedule.id, schedule.scheduledAction);
              }
              : null,
            (() => {
              return (schedule: ISchedule) => IScheduleInfo.getScheduleDescription(schedule);
            })(),
            subscriptionOnExtractRefreshFn,
            (row: DataGrid.Row) => {
              const nonScheduledAcceleration = row.data.schedule === undefined;
              return FeatureFlags.workbookAccelerationUIEnabled && nonScheduledAcceleration;
            })
        };

      case CommonColumn.SubscribedBy:
        return {
          name: CommonColumn.SubscribedBy,
          dataKey: OrderKeys.creatorName,
          sortable: true,
          direction: 'asc',
          headerText: Messages.column_task_subscribed_by(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.LinkCell(this.generateLinkForSubscribedBy),
        };

      case CommonColumn.LastUpdate:
        if (FeatureFlags.suspendSubscriptions) {
          const lastUpdateCell = StyledCellFormatter(DataGridCells.InteractiveIconTooltipCell((row: DataGrid.Row) => {
            const subscription = row.data as ISubscriptionInfo;
            const dateTime = subscription.lastSentAt;
            const dateTimeText = dateTime ? Formatters.formatMediumDateTimeWithoutSeconds(dateTime) : Messages.never();
            if (subscription.suspended) {
              // edge case: subscription was suspended but there is no last sent at date
              const subscriptionMessage = dateTime ? Messages.subscriptionSuspended({ SUSPENDED_DATE: dateTimeText }) : Messages.suspended();
              return {
                text: subscriptionMessage,
                title: Messages.subscriptionTooltipTitle(),
                iconClass: 'tb-react-dg-hcol tb-icon-server-alert-list-suspended',
                tooltipText: 'subscriptionSuspendedMessage',
                tooltipReplacements: { ERROR_MESSAGE: () => subscription.lastRunError ? subscription.lastRunError : '' },
                tooltipLinkText: Messages.learnMore(),
                tooltipLink: HelpService.getServerHelpUrlByCategory('sub-suspend')
              };
            } else if (subscription.failed) {
              return {
                text: Messages.subscriptionFailed({ FAILED_DATE: dateTimeText }),
                title: Messages.subscriptionTooltipTitle(),
                iconClass: 'tb-react-dg-hcol tb-icon-dialogs-status-alert',
                tooltipText: 'subscriptionFailedMessage',
                tooltipReplacements: { ERROR_MESSAGE: () => subscription.lastRunError ? subscription.lastRunError : '' },
                tooltipLinkText: Messages.learnMore(),
                tooltipLink: HelpService.getServerHelpUrlByCategory('sub-suspend')
              };
            } else {
              // a non-failed, non-suspended subscription
              return {
                text: dateTimeText,
                title: Messages.subscriptionTooltipTitle()
              };
            }
          }, this.translate.instant.bind(this.translate)));

          return {
            name: CommonColumn.LastUpdate,
            dataKey: OrderKeys.lastSentAt,
            sortable: true,
            headerText: Messages.column_task_last_update(),
            direction: 'desc',
            headerFormatter: sortableHeaderFormatter,
            cellFormatter: lastUpdateCell
          };
        } else {
          return {
            name: CommonColumn.LastUpdate,
            dataKey: OrderKeys.lastSentAt,
            sortable: true,
            headerText: Messages.column_task_last_update(),
            direction: 'desc',
            headerFormatter: sortableHeaderFormatter,
            cellFormatter: DataGridCells.DateTimeCell('lastSentAt', Messages.never())
          };
        }

      case CommonColumn.LastRunAt:
        return {
          name: CommonColumn.LastRunAt,
          dataKey: OrderKeys.lastRunAt,
          sortable: true,
          headerText: Messages.column_task_last_update(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.DateTimeCell('lastRunAt', Messages.never())
        };

      case CommonColumn.LastRunAtExtractRefreshes:
        return this.getLastRunAtExtractRefreshes(sortableHeaderFormatter);
      case CommonColumn.RunNextAtSubscription:
        return {
          name: CommonColumn.RunNextAt,
          dataKey: OrderKeys.runNextAt,
          sortable: true,
          headerText: Messages.column_task_next_update(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.SubscriptionNextUpdateDescription()
        };

      case CommonColumn.RunNextAt:
        return {
          name: CommonColumn.RunNextAt,
          dataKey: OrderKeys.runNextAt,
          sortable: true,
          headerText: Messages.column_task_next_update(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.DateTimeCell('runNextAt', Messages.disabled())
        };

      case CommonColumn.TaskType:
        const taskColumn = {
          name: CommonColumn.TaskType,
          dataKey: OrderKeys.type,
          sortable: true,
          headerText: Messages.column_task_type(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.PlainTextCellWithTransform('type',
            (type: string) => type === ExtractTaskType.IncrementExtract
              ? Messages.extractTasksIncremental()
              : Messages.extractTasksFull())
        };

        return taskColumn;

      case CommonColumn.RemoteRefreshType:
        const remoteRefreshColumn = {
          name: CommonColumn.RemoteRefreshType,
          dataKey: OrderKeys.refreshType,
          sortable: true,
          headerText: Messages.column_task_type(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.PlainTextCellWithTransform('refreshType',
            (refreshType: string) => refreshType === RemoteRefreshTypes.Incremental
              ? Messages.remoteRefreshScheduleTypeNameIncremental()
              : Messages.remoteRefreshScheduleTypeNameFull())
        };
        return remoteRefreshColumn;

      case CommonColumn.Priority:
        return {
          name: CommonColumn.Priority,
          dataKey: OrderKeys.priority,
          sortable: true,
          direction: 'asc',
          headerText: Messages.column_task_priority(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('priority'))
        };

      case CommonColumn.RemoteRefreshPriority:
        return {
          name: CommonColumn.RemoteRefreshPriority,
          dataKey: OrderKeys.priority,
          sortable: false,
          headerText: Messages.column_task_priority(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.FixedValueCell(Messages.notApplicable()),
        };

      case CommonColumn.Definition:
        return {
          name: CommonColumn.Definition,
          dataKey: OrderKeys.definition,
          sortable: true,
          direction: 'asc',
          headerText: Messages.column_remote_schedule_definition(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.ScheduleFrequencyDescriptionCell((() => {
            return (schedule: ISchedule) => IScheduleInfo.getScheduleDescription(schedule);
          })())
        };

      case CommonColumn.OutputStep:
        return {
          name: 'output-col',
          dataKey: OrderKeys.flowOutputStepName,
          headerText: Messages.flowRunHistoryColumn_output(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          direction: 'asc',
          cellFormatter: DataGridCells.PlainTextCell('outputName')
        };

      case CommonColumn.OutputSteps:
        return {
          name: CommonColumn.OutputSteps,
          dataKey: OrderKeys.flowOutputSteps,
          headerText: Messages.outputSteps(),
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          direction: 'asc',
          // "TFSID 843765: Don't show detailed output step info on Server pages because we can't reliably fetch data connections"
          cellFormatter: this.params.inServerArea ? SchedulesGridCells.ServerOutputsCell(this.translate.instant.bind(this.translate))
            : DataGridCells.FlowTaskOutputStepsWithIconsCell()
        };

      case CommonColumn.FlowName:
        const flowTaskNameColumn = {
          name: CommonColumn.FlowName,
          dataKey: OrderKeys.flowName,
          headerText: Messages.flow(),
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          direction: 'asc',
          cellFormatter: DataGridCells.LinkCell(this.generateLinkForTasksFlowName)
        };
        return flowTaskNameColumn;

      case CommonColumn.FlowFileType:
        return {
          name: CommonColumn.FlowFileType,
          dataKey: OrderKeys.flowFileType,
          sortable: false,
          headerText: Messages.column_flow_file_type(),
          headerFormatter: sortableHeaderFormatter,
          direction: 'asc',
          cellFormatter: DataGridCells.PlainTextCell(OrderKeys.flowFileType),
        };

      case CommonColumn.RunFlowTasksStatus:
        return {
          name: CommonColumn.RunFlowTasksStatus,
          dataKey: OrderKeys.status,
          headerText: Messages.flowRunHistoryColumn_status(),
          sortable: false,
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.FlowTaskStatusWithIconsCell(this.params.urls, this.params.flowFailureThresholdForRunPrevention)
        };

      case CommonColumn.LastRunAtFlows:
        return {
          name: CommonColumn.LastRunAtFlows,
          dataKey: OrderKeys.lastRunAt,
          sortable: false,
          headerText: Messages.lastRun(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.DateTimeCell('lastRunAt', Messages.never())
        };

      case CommonColumn.RunNextAtFlows:
        return {
          name: CommonColumn.RunNextAtFlows,
          dataKey: OrderKeys.runNextAt,
          sortable: true,
          headerText: Messages.nextScheduledRun(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.DateTimeCell('runNextAt', Messages.disabled())
        };

      case CommonColumn.RunType:
        return {
          name: 'schedule-col',
          dataKey: 'schedule',
          headerText: Messages.flowRunHistoryColumn_runTypeTitle(),
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          direction: 'asc',
          cellFormatter: FeatureFlags.incrementalRefresh ? DataGridCells.RefreshTypeCell() : DataGridCells.RunTypeCell()
        };

      case CommonColumn.RunStartedAt:
        return {
          name: 'start-col',
          dataKey: OrderKeys.startedAt,
          headerText: Messages.flowRunHistoryColumn_runStart(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          direction: 'desc',
          cellFormatter: DataGridCells.DateTimeCell('startedAt')
        };

      case CommonColumn.RunEndedAt:
        return {
          name: 'end-col',
          dataKey: OrderKeys.completedAt,
          headerText: Messages.flowRunHistoryColumn_runEnd(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          direction: 'desc',
          cellFormatter: DataGridCells.DateTimeCell('completedAt')
        };

      case CommonColumn.RunDuration:
        return {
          name: 'duration-col',
          dataKey: 'duration',
          headerText: Messages.flowRunHistoryColumn_duration(),
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.DurationCell('startedAt', 'completedAt'),
        };

      case CommonColumn.RunStatus:
        return {
          name: 'status-col',
          dataKey: OrderKeys.status,
          headerText: Messages.flowRunHistoryColumn_status(),
          sortable: true,
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.PlainTextCellWithIcon((row: any) => {
            const rowData = row as FlowOutputStepRunListItemInfo;
            return {
              text: localizeFlowRunHistoryStatus(rowData.status),
              iconClass: getFlowOutputStepRunIconClass(rowData.status)
            };
          })
        };

      case CommonColumn.RunRowsProduced:
        return {
          name: 'rows-produced-col',
          dataKey: OrderKeys.rowsProduced,
          headerText: Messages.flowRunHistoryColumn_rowsGeneratedTitle(),
          sortable: true,
          direction: 'desc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('rowsProduced')),
        };

      case CommonColumn.RunErrors:
        const siteUrls = new SiteUrls({
          siteUrlName: this.server.sessionSiteUrlName(),
          queryString: window.location.search,
        });
        return {
          name: CommonColumn.RunErrors,
          dataKey: 'errors',
          headerText: Messages.flowRunHistoryColumn_errors(),
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.FlowRunHistoryErrorsCell(siteUrls, true),
        };

      case CommonColumn.Id:
        const idFormatter = (props: BodyCell.Props) => {
          const backgroundJob = props.row.data as IBackgroundJobInfo;
          return <BackgroundJobExtendedInfoDialogCell {...backgroundJob} />;
        };
        return {
          name: 'id-col',
          dataKey: OrderKeys.jobId,
          headerText: Messages.column_backgroundJob_id(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          // We avoid using factories as they are deprecated
          cellFormatter: idFormatter as React.Factory<any>,
        };
      case CommonColumn.Status:
        const statusFormatter = DataGridCells.InteractiveImageIconTooltipCell((row: DataGrid.Row) => {
          const backgroundJob = row.data as unknown as IBackgroundJob;
          return {
            text: localizeBackgroundJobState(backgroundJob),
            title: Messages.subscriptionTooltipTitle(),
            iconClass: 'tb-react-dg-hcol',
            iconUrl: getIconForBackgroundJob(backgroundJob),
            iconTitle: localizeBackgroundJobState(backgroundJob),
            tooltipText: getBackgroundJobStatusTooltip(backgroundJob),
            tooltipLinkText: Messages.learnMore(),
            tooltipLink: HelpService.getServerHelpUrlByCategory('jobmgmt')
          };
        });
        return {
          name: 'status-col',
          dataKey: OrderKeys.status,
          headerText: Messages.column_backgroundJob_status(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: statusFormatter,
          preferredWidth: 100,
        };
      case CommonColumn.CurrentRunTime:
        return {
          name: 'runtime-col',
          dataKey: OrderKeys.currentRunTime,
          headerText: Messages.column_backgroundJob_runTime(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: RightAlignedCell(DataGridCells.LocalizedFloat((job => (job[OrderKeys.currentRunTime] / 60)))),
        };
      case CommonColumn.CurrentQueueTime:
        return {
          name: 'queuetime-col',
          dataKey: OrderKeys.currentQueueTime,
          headerText: Messages.column_backgroundJob_queueTime(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: RightAlignedCell(DataGridCells.LocalizedFloat((job => (job[OrderKeys.currentQueueTime] / 60)))),
        };
      case CommonColumn.AverageRunTime:
        return {
          name: 'average-runtime-col',
          headerText: Messages.column_backgroundJob_averageRunTime(),
          dataKey: OrderKeys.averageRunTime,
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: RightAlignedCell(DataGridCells.LocalizedFloat((job => (job[OrderKeys.averageRunTime] / 60)))),
          preferredWidth: 110,
        };
      case CommonColumn.AverageQueueTime:
        return {
          name: 'average-queuetime-col',
          headerText: Messages.column_backgroundJob_averageQueueTime(),
          dataKey: OrderKeys.averageQueueTime,
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: RightAlignedCell(DataGridCells.LocalizedFloat((job => (job[OrderKeys.averageQueueTime] / 60)))),
          preferredWidth: 125,
        };
      case CommonColumn.BackgroundJobType:
        return {
          name: 'taskType-col',
          dataKey: OrderKeys.taskType,
          headerText: Messages.column_backgroundJob_taskType(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.PlainTextCellWithTransform(OrderKeys.taskType, localizeBackgroundJobTaskType),
        };
      case CommonColumn.WorkbookDatasourceName:
        const workbookDatasourceNameColumn = {
          name: CommonColumn.WorkbookDatasourceName,
          dataKey: OrderKeys.targetName,
          growToFit: isScheduleSpace(this.params.spaceInfo),
          headerText: Messages.column_task_workbook_data_source(),
          sortable: true,
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.LinkCell(this.generateLinkForWorkbookDatasourceName),
        };
        return workbookDatasourceNameColumn;
      case CommonColumn.MetricValue:
        return {
          name: CommonColumn.MetricValue,
          dataKey: OrderKeys.metricValue,
          headerText: Messages.column_metrics_value(),
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.MetricValueCell(),
        };
      case CommonColumn.MetricConnectedView:
        return {
          name: CommonColumn.MetricConnectedView,
          dataKey: OrderKeys.connectedViewName,
          headerText: Messages.column_metrics_viewName(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.LinkCell(this.generateLinkForMetricConnectedView),
        };
      case CommonColumn.MetricUpdated:
        return {
          name: CommonColumn.MetricUpdated,
          dataKey: OrderKeys.metricUpdated,
          headerText: Messages.column_metrics_lastUpdated(),
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.MetricUpdatedCell(),
          // TFSID: 1039542 - 174px is just wide enough to fit the "Refresh Suspended" text.
          preferredWidth: 174,
        };
      case CommonColumn.BackgroundJobPriority:
        return {
          name: CommonColumn.Priority,
          dataKey: OrderKeys.priority,
          sortable: true,
          direction: 'asc',
          headerText: Messages.column_task_priority(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount(OrderKeys.priority))
        };
      case CommonColumn.JobRequestedTime:
        return {
          name: CommonColumn.JobRequestedTime,
          dataKey: OrderKeys.jobRequestedTime,
          sortable: true,
          headerText: Messages.column_backgroundJob_jobRequestedTime(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.DateTimeCell(OrderKeys.jobRequestedTime, Messages.never())
        };
      case CommonColumn.DataAlertSubject:
        const subjectColumn = {
          name: CommonColumn.DataAlertSubject,
          dataKey: OrderKeys.title,
          sortable: true,
          direction: 'asc',
          growToFit: true,
          headerText: Messages.column_task_subject(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.PlainTextCell('name')
        };
        return subjectColumn;
      case CommonColumn.DataAlertView:
        return {
          name: CommonColumn.DataAlertView,
          dataKey: OrderKeys.targetName,
          sortable: true,
          headerText: Messages.column_view_header(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.LinkCell((row: DataGrid.Row) => {
            const data = row.data as IDataAlertInfo;
            data.deviceType = data.deviceType.toString().toLowerCase() as DeviceType;
            return {
              text: DataAlertDataGridCells.getViewName(data),
              url: DataAlertDataGridCells.getViewUrl(data, this.params.urls),
              iconClassName: 'tb-grid-icon-view',
              iconTitle: Messages.view()
            };
          })
        };
      case CommonColumn.DataAlertRecepient:
        const numRecipientsShown = 2;
        return {
          name: CommonColumn.DataAlertRecepient,
          dataKey: 'displayRecipients',
          sortable: false,
          headerText: Messages.column_recipients(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataAlertDataGridCells.RecipientsListCell({
            numRecipientsShown,
            siteUrls: this.params.urls,
            server: this.server,
            onPillClick: (dataAlert: IDataAlertInfo) => this.manageDataAlertRecipientsDialog.open(this.params.angularContext.scope, dataAlert),
            testId: 'dda-recipients-cell'
          })
        };
      case CommonColumn.DataAlertVisibleTo:
        return {
          name: CommonColumn.DataAlertVisibleTo,
          dataKey: 'isPublic',
          sortable: false,
          headerText: Messages.column_alertPrivacy(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.PlainTextCellWithTransform('isPublic', (isPublic: boolean) => isPublic ? Messages.dataAlertPublic() : Messages.dataAlertPrivate())
        };
      case CommonColumn.DataAlertEmailSent:
        const mapNotificationInterval = DataAlertDataGridCells.createMapNotificationInterval();
        return {
          name: CommonColumn.DataAlertEmailSent,
          dataKey: OrderKeys.notificationIntervalInMinutes,
          sortable: true,
          headerText: Messages.column_notificationFrequency(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.PlainTextCellWithTransform('notificationIntervalInMinutes', mapNotificationInterval)
        };
      case CommonColumn.DataAlertLastChecked:
        return {
          name: CommonColumn.DataAlertLastChecked,
          dataKey: OrderKeys.lastChecked,
          sortable: true,
          headerText: Messages.column_last_checked(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataAlertDataGridCells.buildLastCheckedFormatter(this.translate.instant.bind(this.translate))
        };
      case CommonColumn.DataAlertLast:
        return {
          name: CommonColumn.DataAlertLast,
          dataKey: 'lastAlerted',
          sortable: false,
          headerText: Messages.column_last_alerted(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataAlertDataGridCells.LastAlertCell(Messages.never())
        };
      case CommonColumn.SiteNameLink:
        let siteNameColumn: DataGrid.Column = {
          name: CommonColumn.SiteNameLink,
          dataKey: OrderKeys.name,
          sortable: true,
          headerText: Messages.column_name(),
          direction: 'asc',
          growToFit: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.LinkCell(this.generateLinkForSiteName)
        };
        return siteNameColumn;
      case CommonColumn.SiteUserCount:
        return {
          name: CommonColumn.SiteUserCount,
          dataKey: OrderKeys.userCount,
          sortable: true,
          headerText: Messages.column_userCount(),
          direction: 'asc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('userCount'))
        };
      case CommonColumn.SiteAdminCount:
        return {
          name: CommonColumn.SiteAdminCount,
          dataKey: OrderKeys.adminCount,
          sortable: true,
          headerText: Messages.column_adminCount(),
          direction: 'asc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('adminCount'))
        };
      case CommonColumn.SiteUserQuota:
        return {
          name: CommonColumn.SiteUserQuota,
          dataKey: OrderKeys.userQuota,
          sortable: true,
          headerText: Messages.column_maxUsers(),
          direction: 'asc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(SiteGridCells.userQuotaFormatter())
        };
      case CommonColumn.SiteStorageUsed:
        return {
          name: CommonColumn.SiteStorageUsed,
          dataKey: OrderKeys.storageUsedBytes,
          sortable: true,
          headerText: Messages.column_storageUsed(),
          direction: 'asc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(SiteGridCells.StorageUsedFormatter)
        };
      case CommonColumn.SiteStorageQuota:
        return {
          name: CommonColumn.SiteStorageQuota,
          dataKey: OrderKeys.storageQuotaBytes,
          sortable: true,
          headerText: Messages.column_storageQuota(),
          direction: 'asc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(SiteGridCells.StorageQuotaFormatter)
        };
      case CommonColumn.SiteStatus:
        return {
          name: CommonColumn.SiteStatus,
          dataKey: OrderKeys.availability,
          sortable: true,
          headerText: Messages.column_status(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.PlainTextCellWithTransform('availability', this.translate.instant.bind(this.translate)),
        };
      case CommonColumn.SiteMetricsEnabled:
        return {
          name: CommonColumn.SiteMetricsEnabled,
          dataKey: OrderKeys.metricsEnabled,
          sortable: true,
          headerText: Messages.column_metrics(),
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: CenterAlignedCell(SiteGridCells.Checkmark('settings.metricsEnabled')),
        };
      case CommonColumn.SiteAuthoringEnabled:
        return {
          name: CommonColumn.SiteAuthoringEnabled,
          dataKey: OrderKeys.authoringEnabled,
          sortable: true,
          headerText: Messages.column_webAuthoring(),
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: CenterAlignedCell(SiteGridCells.Checkmark('settings.authoringEnabled')),
        };
      case CommonColumn.SiteSheetImageEnabled:
        return {
          name: CommonColumn.SiteSheetImageEnabled,
          dataKey: OrderKeys.sheetImageEnabled,
          sortable: true,
          headerText: Messages.column_sheetImage(),
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: CenterAlignedCell(SiteGridCells.Checkmark('siteAdminSettings.sheetImageEnabled')),
        };
      case CommonColumn.SiteEditMembership:
        return {
          name: CommonColumn.SiteEditMembership,
          dataKey: OrderKeys.siteAdminsCanAddUsers,
          sortable: true,
          headerText: Messages.column_editMembership(),
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: CenterAlignedCell(SiteGridCells.Checkmark('settings.siteAdminsCanAddUsers')),
        };
      case CommonColumn.SiteExtractEncryption:
        return {
          name: CommonColumn.SiteExtractEncryption,
          dataKey: OrderKeys.extractEncryptionMode,
          sortable: true,
          headerText: Messages.column_extractEncryptionMode(),
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: CenterAlignedCell(SiteGridCells.ExtractEncryptionModeCell()),
        };
      case CommonColumn.SiteAskDataMode:
        return {
          name: CommonColumn.SiteAskDataMode,
          dataKey: OrderKeys.askDataMode,
          sortable: false,
          headerText: Messages.column_askDataMode(),
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: CenterAlignedCell(SiteGridCells.AskDataModeCellFormatter()),
        };
      case CommonColumn.SiteGuestAccessEnabled:
        return {
          name: CommonColumn.SiteGuestAccessEnabled,
          dataKey: OrderKeys.guestAccessEnabled,
          sortable: true,
          headerText: Messages.column_guestAccessEnabled(),
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: CenterAlignedCell(SiteGridCells.Checkmark('settings.guestAccessEnabled')),
        };
      case CommonColumn.ScheduleFrequency:
        return {
          name: CommonColumn.ScheduleFrequency,
          dataKey: OrderKeys.scheduleType,
          sortable: true,
          headerText: Messages.column_schedule_frequency(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: SchedulesGridCells.FrequencyCell()
        };
      case CommonColumn.ScheduleTaskType:
        return {
          name: CommonColumn.ScheduleTaskType,
          dataKey: OrderKeys.scheduledAction,
          sortable: true,
          headerText: Messages.column_schedule_type(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: SchedulesGridCells.TaskTypeCell()
        };
      case CommonColumn.ScheduleTasks:
        return {
          name: CommonColumn.ScheduleTasks,
          dataKey: OrderKeys.taskCount,
          sortable: true,
          headerText: Messages.column_schedule_task_count(),
          direction: 'asc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('taskCount'))
        };
      case CommonColumn.ScheduleExecution:
        return {
          name: CommonColumn.ScheduleExecution,
          dataKey: OrderKeys.parallel,
          sortable: true,
          headerText: Messages.column_schedule_behavior(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: SchedulesGridCells.ExecutionCell()
        };
      case CommonColumn.ScheduleNextRunAt:
        return {
          name: CommonColumn.ScheduleNextRunAt,
          dataKey: OrderKeys.runNextAt,
          sortable: true,
          headerText: Messages.column_schedule_next_run(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.DateTimeCell('runNextAt', Messages.disabled())
        };
      case CommonColumn.SiteName:
        return {
          name: CommonColumn.SiteName,
          dataKey: OrderKeys.siteName,
          sortable: true,
          headerText: Messages.column_task_site(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.PlainTextCellWithTransform('site',
            (site: ISiteNameWithId) => site.name)
        };
      case CommonColumn.ScheduleName:
        let scheduleNameColumn: DataGrid.Column = {
          name: CommonColumn.ScheduleName,
          dataKey: OrderKeys.name,
          sortable: true,
          headerText: Messages.column_name(),
          direction: 'asc',
          growToFit: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.LinkCell(this.generateLinkForScheduleName)
        };
        return scheduleNameColumn;
      case CommonColumn.GroupName:
        let groupNameColumn: DataGrid.Column = {
          name: CommonColumn.GroupName,
          dataKey: OrderKeys.name,
          sortable: true,
          headerText: Messages.column_name(),
          direction: 'asc',
          growToFit: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: GroupDataGridCells.ProfilePictureCell(
            (row: DataGrid.Row) => {
              const group = row.data as IGroupInfo;
              return {
                profileDisplayName: group.name,
                imageUrl: '',
                divShape: ProfilePicture.Shape.Square,
                isAllUsersGroup: group.isAllUsersGroup
              };
            },
            (row: DataGrid.Row) => {
              return this.generateLinkForGroupName(row);
            }
          )
        };
        return groupNameColumn;
      case CommonColumn.GroupDomain:
        return {
          name: CommonColumn.GroupDomain,
          dataKey: OrderKeys.domainName,
          sortable: true,
          headerText: Messages.column_domainName(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.PlainTextCell(OrderKeys.domainName)
        };
      case CommonColumn.GroupUserCount:
        return {
          name: CommonColumn.GroupUserCount,
          dataKey: OrderKeys.userCount,
          sortable: true,
          headerText: Messages.column_userCount(),
          direction: 'asc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('userCount'))
        };
      case CommonColumn.GroupMinSiteRole:
        return {
          name: CommonColumn.GroupMinSiteRole,
          dataKey: OrderKeys.minimumSiteRole,
          sortable: true,
          headerText: Messages.column_minimumSiteRole(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: GroupDataGridCells.MinimumSiteRoleCell()
        };
      case CommonColumn.GroupLicenseOnSignIn:
        return {
          name: CommonColumn.GroupLicenseOnSignIn,
          dataKey: OrderKeys.grantLicenseMode,
          sortable: true,
          headerText: Messages.column_license_on_signin(),
          direction: 'asc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: CenterAlignedCell(SiteGridCells.LicenseOnSignInFormatter),
        };
      case CommonColumn.SiteUserName:
        return {
          name: CommonColumn.SiteUserName,
          dataKey: OrderKeys.username,
          sortable: true,
          headerText: Messages.column_username(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.PlainTextCell(OrderKeys.username)
        };
      case CommonColumn.SiteUserRole:
        return {
          name: CommonColumn.SiteUserRole,
          dataKey: OrderKeys.siteRole,
          sortable: true,
          headerText: Messages.column_site_role(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.PlainTextCellWithTransform(
            'siteRole',
            SiteRolesHelper.siteRoleToName
          )
        };
      case CommonColumn.SiteUserGroupCount:
        return {
          name: CommonColumn.SiteUserGroupCount,
          dataKey: OrderKeys.groupCount,
          sortable: true,
          headerText: Messages.column_groupCount(),
          direction: 'asc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('groupCount'))
        };
      case CommonColumn.SiteUserLastSignin:
        return {
          name: CommonColumn.SiteUserLastSignin,
          dataKey: OrderKeys.lastSignIn,
          sortable: true,
          headerText: Messages.column_last_sign_in(),
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.DateTimeCell('lastSignIn')
        };
      case CommonColumn.SiteUserDomainName:
        return {
          name: CommonColumn.SiteUserDomainName,
          dataKey: OrderKeys.domainName,
          sortable: true,
          headerText: Messages.column_domainName(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.PlainTextCell(OrderKeys.domainName)
        };
      case CommonColumn.SiteUserAuthentication:
        return {
          name: CommonColumn.SiteUserAuthentication,
          dataKey: CommonColumn.SiteUserAuthentication,
          sortable: false,
          headerText: Messages.column_authentication(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: UserDataGridCells.AuthenticationCell
        };
      case CommonColumn.SiteUserDisplayName:
        return this.siteUserDataGridColumn(sortableHeaderFormatter);
      case CommonColumn.SearchUserOwnedViewsCount:
        return {
          name: CommonColumn.SearchUserOwnedViewsCount,
          dataKey: CommonColumn.SearchUserOwnedViewsCount,
          sortable: false,
          direction: 'asc',
          headerText: Messages.column_owned_views(),
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount((item: any) => item.ownedItemCounts.viewCount))
        };
      case CommonColumn.SearchUserOwnedWorkbooksCount:
        return {
          name: CommonColumn.SearchUserOwnedWorkbooksCount,
          dataKey: CommonColumn.SearchUserOwnedWorkbooksCount,
          sortable: false,
          direction: 'asc',
          headerText: Messages.column_owned_workbooks(),
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount((item: any) => item.ownedItemCounts.workbookCount))
        };
      case CommonColumn.SearchUserOwnedDatasourcesCount:
        return {
          name: CommonColumn.SearchUserOwnedDatasourcesCount,
          dataKey: CommonColumn.SearchUserOwnedDatasourcesCount,
          sortable: false,
          direction: 'asc',
          headerText: Messages.column_owned_datasources(),
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount((item: any) => item.ownedItemCounts.datasourceCount))
        };
      case CommonColumn.ServerUserDisplayName:
        return {
          name: CommonColumn.ServerUserDisplayName,
          dataKey: OrderKeys.displayName,
          sortable: true,
          direction: 'asc',
          growToFit: true,
          headerText: Messages.column_fullName(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: UserDataGridCells.ProfilePictureCell(
            (row: DataGrid.Row) => {
              const user = row.data as IServerUserInfo;
              return {
                profileDisplayName: user.displayName,
                imageUrl: user.userImageUrl,
                divShape: ProfilePicture.Shape.Circle
              };
            },
            (row: DataGrid.Row) => {
              const user = row.data as IServerUserInfo;
              const disabled = user.readOnly || user.maxSiteRole === SiteRoles.Guest;
              return {
                text: user.displayName,
                url: disabled ? null : ServerUrls.getInstance().userSettingsUrl(user.domainName, user.username),
                style: disabled ? { color: CommonStyles.Colors.Grey102, textDecoration: 'none', pointerEvents: 'none' } : null
              };
            }
          )
        };
      case CommonColumn.ServerUserMaxSiteRole:
        return {
          name: CommonColumn.ServerUserMaxSiteRole,
          dataKey: OrderKeys.maxSiteRole,
          sortable: true,
          direction: 'asc',
          headerText: Messages.column_max_site_role(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.PlainTextCellWithTransform(
            'maxSiteRole',
            SiteRolesHelper.siteRoleToName
          )
        };
      case CommonColumn.ServerUserLicenseLevel:
        return {
          name: CommonColumn.ServerUserLicenseLevel,
          dataKey: OrderKeys.licenseLevel,
          sortable: true,
          direction: 'asc',
          headerText: Messages.column_license_level(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.PlainTextCellWithTransform(
            'subscriptionLicensingRole',
            this.licenseLevelTransform(this.translate.instant.bind(this.translate))
          )
        };
      case CommonColumn.ServerUserSiteCounts:
        return {
          name: CommonColumn.ServerUserSiteCounts,
          dataKey: OrderKeys.siteCount,
          sortable: true,
          direction: 'asc',
          headerText: Messages.column_site_count(),
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('siteCount'))
        };
      case CommonColumn.CollectionItemCount:
        return {
          name: CommonColumn.CollectionItemCount,
          dataKey: OrderKeys.itemCount,
          headerText: Messages.collectionItemCountColumn(),
          sortable: false, // disabled until we figure out how to handle view item permissions
          direction: 'desc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('permissionedItemCount')),
        };
      case CommonColumn.CollectionVisibility:
        return {
          name: CommonColumn.CollectionVisibility,
          dataKey: OrderKeys.visibility,
          headerText: Messages.collectionVisibilityColumn(),
          sortable: true,
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.PlainTextCellWithTransform(
            'visibility',
            (visibility: CollectionVisibility) => visibility === 'private'
              ? Messages.collectionVisibilityPrivate()
              : Messages.collectionVisibilityPublic(),
          )
        };

      case CommonColumn.SharedBy:
        return {
          name: CommonColumn.SharedBy,
          dataKey: OrderKeys.sharedBy,
          headerText: Messages.sharedBy(),
          sortable: false,
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: UserDataGridCells.ProfilePictureCell(
            (row: DataGrid.Row) => {
              const share = row.data as ISharedWithMeContentInfo;
              return {
                profileDisplayName: share.sharer.displayName,
                imageUrl: share.sharer.isDeleted ? null : share.sharer.userImageUrl,
                divShape: ProfilePicture.Shape.Circle,
              };
            },
            (row: DataGrid.Row) => {
              const share = row.data as ISharedWithMeContentInfo;
              const disabled = share.sharer.isDeleted; // TODO: update this when API signature is confirmed
              return {
                text: disabled ? Messages.sharedWithMeRemovedUser({USERNAME: share.sharer.username}) : share.sharer.displayName,
                url: disabled ? null : this.params.urls.userUrl(share.sharer.username, share.sharer.domainName),
                style: disabled ? { color: CommonStyles.Colors.Grey102, textDecoration: 'none', pointerEvents: 'none' } : null,
              };
            }
          )
        };

      case CommonColumn.SharedCustomizedView:
        return {
          name: CommonColumn.SharedCustomizedView,
          dataKey: OrderKeys.sharedCustomizedView,
          headerText: Messages.sharedWithMeSharedVersionsTitle(),
          resizable: false,
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.SharedViewsCell(
            (row: DataGrid.Row) => {
              const share = row.data as ISharedWithMeContentInfo;
              return {
                showButton: share.isShareActivityIconVisible && !share.isAccessDenied,
                contentId: share.id,
              };
            },
            this.params.urls,
          ),
        };

      case CommonColumn.SharedAt:
        return {
          name: CommonColumn.SharedAt,
          dataKey: OrderKeys.sharedAt,
          headerText: Messages.sharedAt(),
          sortable: false,
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.DateTimeCell(OrderKeys.sharedAt),
        };

      case CommonColumn.SharedWith:
        return {
          name: CommonColumn.SharedWith,
          dataKey: OrderKeys.sharedWith,
          headerText: Messages.sharedWith(),
          sortable: true,
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataGridCells.PlainTextCell(OrderKeys.sharedWith),
        };

        case CommonColumn.SpeedUp:
          return {
            name: CommonColumn.SpeedUp,
            dataKey: 'speedUp',
            headerText: Messages.speedUp(),
            sortable: false,
            headerFormatter: sortableHeaderFormatter,
            cellFormatter: DataGridCells.PlainTextCell('speedUp'),
          };

          case CommonColumn.AvgNonAcceleratedPLT:
          return {
            name: CommonColumn.AvgNonAcceleratedPLT,
            dataKey: 'avgNonAcceleratedPLT',
            headerText: Messages.avgNonAcceleratedPLT(),
            sortable: false,
            headerFormatter: sortableHeaderFormatter,
            cellFormatter: DataGridCells.PlainTextCell('avgNonAcceleratedPLT'),
          };

          case CommonColumn.AvgAcceleratedPLT:
          return {
            name: CommonColumn.AvgAcceleratedPLT,
            dataKey: 'avgAcceleratedPLT',
            headerText: Messages.avgAcceleratedPLT(),
            sortable: false,
            headerFormatter: sortableHeaderFormatter,
            cellFormatter: DataGridCells.PlainTextCell('avgAcceleratedPLT'),
          };

      default:
        throw new Error('Column not yet implemented: ' + columnDefinition);
    }
  }

  private getLastRunAtExtractRefreshes(sortableHeaderFormatter: React.Factory<any>): DataGrid.Column {

    const lastUpdateCell = DataGridCells.InteractiveIconTooltipCell((row: DataGrid.Row) => {
      const extractTaskInfo = row.data as IExtractTaskInfo;
      const dateTime = extractTaskInfo.lastRunAt;
      const dateTimeText = dateTime ? Formatters.formatMediumDateTimeWithoutSeconds(dateTime) : Messages.never();
      if (extractTaskInfo.state === 'Disabled') {
        const message = dateTime ? Messages.subscriptionSuspended({ SUSPENDED_DATE: dateTimeText }) : Messages.suspended();
        return {
          text: message,
          title: Messages.extractRefreshTooltipTitle(),
          iconClass: 'tb-react-dg-hcol tb-icon-server-alert-list-suspended',
          tooltipText: 'extractRefreshPausedMessage',
          tooltipLinkText: Messages.learnMore(),
          tooltipLink: HelpService.getServerHelpUrlByCategory('extract-auto-suspend')
        };
      } else {
        return {
          text: dateTimeText,
          title: Messages.extractRefreshTooltipTitle(),
        };
      }
    }, this.translate.instant.bind(this.translate));

    return {
      name: CommonColumn.LastRunAtExtractRefreshes,
      dataKey: OrderKeys.lastRunAt,
      headerText: Messages.column_task_last_update(),
      direction: 'asc',
      headerFormatter: sortableHeaderFormatter,
      cellFormatter: lastUpdateCell
    };
  }

  private licenseLevelTransform(translate: IReactTranslateService): (licenseRole: string) => string {
    return (licenseRole: string) => licenseRole ? translate('license_' + licenseRole) : '';
  }

  private isAccessDenied = (row: DataGrid.Row): boolean => {
    return !!row.data.isAccessDenied;
  }

  private isEmbeddedDatasource = (row: DataGrid.Row): boolean => {
    return row.data.contentType === DatasourceDefinition.contentType && !!row.data.workbook;
  }

  private isUnifiedDataSource = (row: DataGrid.Row): boolean => {
    return DataSources.isUnifiedDatasource(row.data as IContentInfo);
  }

  private isCustomView = (row: DataGrid.Row): boolean => {
    return row.data.contentType === CustomViewDefinition.itemType;
  }

  private isMetric = (row: DataGrid.Row): boolean => {
    return row.data.contentType === MetricDefinition.contentType;
  }

  private hasDataQualityIndicator = (row: DataGrid.Row): boolean => {
    if (row.data.contentType === DatasourceDefinition.contentType) {
      return DataSources.hasDataQualityWarning(getCommonDatasourceInfo(row.data as IContentInfo | IDataSourceInfo));
    } else if (row.data.contentType === FlowDefinition.contentType) {
      return Flows.hasDataQualityWarning(row.data as FlowBaseInfo);
    } else {
      return false;
    }
  }

  private isCertifiedDatasource = (row: DataGrid.Row): boolean => {
    if (row.data.contentType !== DatasourceDefinition.contentType) {
      return false;
    }
    return DataSources.isCertified(getCommonDatasourceInfo(row.data as IContentInfo | IDataSourceInfo));
  }

  private isFavoritable = (row: DataGrid.Row): boolean => {
    return !this.isEmbeddedDatasource(row);
  }

  /* If the data is a unified data source, the id will be the connection id which does not work for favoriting */
  private getItemForFavoriting(row: DataGrid.Row): IFavoritableItemInfo {
    // If the data is a unified data source, the id will be the connection id which does not work for favoriting
    const idForFavoriting = this.isUnifiedDataSource(row) ? (row.data as IDataSourceInfo).idForFavoriting : row.data.id;
    return {
      id: row.data.id,
      contentType: row.data.contentType,
      name: row.data.name,
      idForFavoriting,
    };
  }

  private onAddFavorite = (row: DataGrid.Row): void => {
    this.params.addContentFavorite(this.getItemForFavoriting(row));
  }

  private onRemoveFavorite = (row: DataGrid.Row): void => {
    this.params.removeContentFavorite(this.getItemForFavoriting(row));
  }

  private isRowSelected = (row: DataGrid.Row): boolean => {
    return !!this.params.selectedItems[getKeyForExplorableItem(row.data as IExplorableItemTypeInfo)];
  }

  private onSelectedCheckboxClicked = (row: DataGrid.Row): void => {
    const item = row.data as IContentInfo;

    if (this.isRowSelected(row)) {
      this.params.deselectItem(item);
    } else {
      this.params.selectItem(item);
    }
  }

  private checkboxCellTestId = (row: DataGrid.Row): string => {
    return 'select-' + row.key;
  }

  private favoriteCellTestId = (row: DataGrid.Row): string => {
    return 'favorite-' + row.key;
  }

  private dataConnectionDataSourceCellTestId = (row: DataGrid.Row): string => {
    return 'dataConnectionDataSource-' + row.key;
  }

  private contentTypeImageUrlForRow = (row: DataGrid.Row): string => {

    if (row.data.contentType === DatasourceDefinition.contentType) {
      return DataSources.getIconForDatasourceType(getCommonDatasourceInfo(row.data as IContentInfo | IDataSourceInfo), 'medium');
    }

    return ExplorableItemDefinitions[row.data.contentType as ExplorableItemType].listViewIconUrl;
  }

  private contentTypeTitleForIconForRow = (row: DataGrid.Row): string => {
    const itemDefintion = ExplorableItemDefinitions[row.data.contentType as ExplorableItemType];
    if (itemDefintion.hasOwnProperty('singularName')) {
      return (itemDefintion as any).singularName();
    }

    return '';
  }

  private getContentUrl = (row: DataGrid.Row): string => {

    if(isSharedWithMeSpace(this.params.spaceInfo)) {
      // Check if there is just a single shared customView
      const customViewUrl = (row.data as ISharedWithMeContentInfo).customView?.url;
      if(customViewUrl) {
        return customViewUrl;
      }
    }

    if (this.isEmbeddedDatasource(row)) {
      return this.params.urls.workbookDatasourcesUrl(row.data.workbook.id);
    }

    if (this.isUnifiedDataSource(row)) {
      return this.params.urls.datasourceUrl(row.data.id);
    }

    if (this.isCustomView(row)) {
      return this.params.urls.redirectToCustomViewUrl(row.data.viewId, row.data.owner.username, row.data.urlId);
    }

    const contentType = row.data.contentType;
    // it only makes sense to generate a url for items that have their own space
    // and hence are SpaceInfoItemType('s)
    if (isSpaceInfoItemType(contentType)) {
      return ContentSpaceDefinitions[contentType].contentSpaceUrl(this.params.urls, row.key, row.data.luid);
    }
  }

  private generateLinkForProject = (row: DataGrid.Row): DataGridCells.ILink => {

    if (row.data.workbook && row.data.workbook.project && row.data.contentType === ViewDefinition.contentType) {
      return {
        text: row.data.workbook.project.name,
        url: this.params.urls.projectUrl(row.data.workbook.project.id)
      };
    }

    if (row.data.project) {
      return {
        text: row.data.project.name,
        url: this.params.urls.projectUrl(row.data.project.id)
      };
    }

    return { text: '', url: '' };
  }

  private generateLinkForDataconnectionDatasource = (row: DataGrid.Row): DataGridCells.ILink => {

    if (!row.data.datasource) {
      // TODO check to see if this is a flow data connection for now return an empty link
      return { url: '', text: '' };
    }

    // we want to display the datasource information here, so grab the data source
    // Throw together object that adheres to DataGrid.Row interface and generate link for this ds
    const modifiedDatasourceRow = { key: row.data.datasource.id, data: DataSources.getDatasourceInfoFromDataConnection(row.data as IDataConnectionInfo) } as DataGrid.Row;
    return this.generateLinkForName(modifiedDatasourceRow, this.dataConnectionDataSourceCellTestId(row));
  }

  // Following logic is borrowed from app/content/ProjectGrid.ts
  private generateLinkForName = (row: DataGrid.Row, testId?: string): DataGridCells.ILink => {
    const name = row.data.name;
    const description = TextileHelper.getPlainTextFromTextile(row.data.description, this.sanitize);
    const title = description ? name + '\n' + description : name;

    return {
      text: name,
      column: CommonColumn.Name,
      title: this.generateTitleForName(row, title),
      url: this.getContentUrl(row),
      displayJustText: isWorkbookSpace(this.params.spaceInfo) && this.isEmbeddedDatasource(row),
      testId: testId ? testId : 'name-' + row.key,
      isAccessDenied: this.isAccessDenied(row),
    };
  }

  // will only render status for flows which support drafts, otherwise will just get the correct url based on the content type
  private generateLinkForNameWithStatus = (row: DataGrid.Row, testId?: string): DataGridCells.ILinkWithStatus => {
    const name = row.data.name;
    const description = TextileHelper.getPlainTextFromTextile(row.data.description, this.sanitize);
    const title = description ? name + '\n' + description : name;

    return {
      text: name,
      column: CommonColumn.Name,
      title: this.generateTitleForName(row, title),
      url: row.data.draftStatus === ContentDraftStatus.unpublished && row.data.contentType === 'flow' ?
        this.params.urls.flowEditorDraftUrl(row.data.draftLuid) :
        this.getContentUrl(row),
      displayJustText: isWorkbookSpace(this.params.spaceInfo) && this.isEmbeddedDatasource(row),
      testId: testId ? testId : 'name-' + row.key,
      draftStatus: row.data.draftStatus,
      openLinkInNewtab: row.data.contentType === 'flow' && row.data.draftStatus === ContentDraftStatus.unpublished
    };
  }

  private generateLinkForTasksFlowName = (row: DataGrid.Row, testId?: string): DataGridCells.ILink => {
    if (row.data.flow) {
      return {
        text: row.data.flow.name,
        url: this.params.urls.flowUrl(row.data.flow.id),
        iconClassName: 'tb-grid-icon-flow',
        iconTitle: Messages.flow()
      };
    }
  }

  private generateLinkForWorkbookDatasourceName = (row: DataGrid.Row, testId?: string): DataGridCells.ILink => {
    const name = row.data && row.data.workbook ? row.data.workbook.name : row.data.datasource.name;
    const urls = this.params.urls.forSiteName(row.data.site.urlName);
    if (row.data.datasource) {
      const datasource: IDataSourceInfo = row.data.datasource;
      return {
        text: datasource.name,
        column: CommonColumn.WorkbookDatasourceName,
        url: urls.datasourceUrl(datasource.id),
        iconClassName: 'tb-grid-icon-datasource',
        iconTitle: Messages.datasource(),
      };
    } else {
      const workbook: IWorkbookInfo = row.data.workbook;
      return {
        text: workbook.name,
        column: CommonColumn.WorkbookDatasourceName,
        url: urls.workbookUrl(workbook.id),
        iconClassName: 'tb-grid-icon-workbook',
        iconTitle: Messages.workbook(),
      };
    }
  }

  private generateTitleForName = (row: DataGrid.Row, title: string): string => {
    if (this.isEmbeddedDatasource(row)) {
      return Messages.datasourceEmbeddedInWorkbook_nameTooltip({
        DATASOURCE_NAME: row.data.name,
        WORKBOOK_NAME: row.data.workbook.name
      });
    }

    return L10n.elideIfLong(title, 511); // IE only allows up to 512 characters in tooltips (unlike Firefox/Chrome)
  }

  private generateLinkForOwner = (row: DataGrid.Row): DataGridCells.ILink => {
    // detail view info's stores owner as part of workbook object
    const useWorkbookOwner = row.data.contentType === ViewDefinition.contentType && (!this.params.showingMixedContent || isRecentsSpace(this.params.spaceInfo));
    const owner = useWorkbookOwner && row.data.workbook ?
      row.data.workbook.owner : row.data.owner;

    if (!owner) {
      return { text: '', url: undefined };
    }

    const displayName = owner.displayName;
    const url = this.params.urls.userUrl(owner.username, owner.domainName);

    return {
      text: displayName,
      url: url,
      style: owner.isHiddenInternalUser ? HiddenUserStyles.HiddenUserStyle : null,
      displayJustText: owner.isHiddenInternalUser,
      testId: 'list-owner-cell-' + row.data.contentType + '-' + row.data.id,
      isAccessDenied: this.isAccessDenied(row),
    };
  }

  private generateLinkForOwnerWithIcon = (row: DataGrid.Row): DataGridCells.ILink => {
    const linkCell: DataGridCells.ILink = this.generateLinkForOwner(row);

    linkCell.iconClassName = 'tb-grid-icon-user';
    linkCell.iconTitle = Messages.user();

    return linkCell;
  }

  private generateLinkForMetricConnectedView = (row: DataGrid.Row): DataGridCells.ILink => {
    if (row.data && row.data.connectedView) {
      const connectedView: IConnectedViewInfo = row.data.connectedView;
      const viewName = connectedView.isViewModified ? connectedView.name + '*' : connectedView.name;

      const url = this.params.urls.viewUrl(connectedView.path);

      return {
        text: viewName,
        url: url
      };
    } else {
      return {
        text: Messages.unavailable(),
        url: '',
        style: GreyedOutTextStyle,
        displayJustText: true
      };
    }
  }

  private generateLinkForSubscribedBy = (row: DataGrid.Row): DataGridCells.ILink => {
    const subscription = row.data as ISubscriptionInfo;
    const subscribedBy = subscription.creator;
    if (!subscribedBy) {
      return { text: '', url: undefined };
    }
    const displayName = subscribedBy.displayName;
    const url = new SpacesSiteUrls({ siteUrlName: subscription.site.urlName }).userUrl(subscribedBy.username, subscribedBy.domainName);
    return {
      text: displayName,
      url: url,
      style: subscribedBy.isHiddenInternalUser ? HiddenUserStyles.HiddenUserStyle as React.CSSProperties : null,
      displayJustText: subscribedBy.isHiddenInternalUser
    };
  }

  private generateLinkForModifiedBy = (row: DataGrid.Row): DataGridCells.ILink => {
    const useWorkbookModifiedBy = (!this.params.showingMixedContent && row.data.contentType === WorkbookDefinition.contentType);
    const modifiedBy = useWorkbookModifiedBy && row.data.workbook ?
      row.data.workbook.modifiedBy : row.data.modifiedBy;

    if (!modifiedBy) {
      return { text: '', url: undefined };
    }

    const displayName = modifiedBy.displayName;
    const url = this.params.urls.userUrl(modifiedBy.username, modifiedBy.domainName);

    return {
      text: displayName,
      url: url,
      style: modifiedBy.isHiddenInternalUser ? HiddenUserStyles.HiddenUserStyle as React.CSSProperties : null,
      displayJustText: modifiedBy.isHiddenInternalUser
    };
  }


  private generateLinkForWorkbook = (row: DataGrid.Row): DataGridCells.ILink => {
    return row.data.workbook && {
      text: row.data.workbook.name,
      url: this.params.urls.workbookUrl(row.data.workbook.id)
    };
  }

  private generateAlert = (row: DataGrid.Row): DataGridCells.IAlert => {
    return {
      type: row.data.contentType,
      alertTitle: Messages.hasAlert(),
      angularContext: this.params.angularContext
    };
  }

  private generateLinkForSiteName = (row: DataGrid.Row): DataGridCells.ILink => {
    const site = row.data as ISiteSettingsForServerAdmin;
    return {
      text: site.settings.name,
      url: this.params.urls.forSiteName(site.settings.urlName).projectsUrl(),
      iconClassName: 'tb-grid-icon-site-stylish',
      iconTitle: Messages.site()
    };
  }

  private generateLinkForScheduleName = (row: DataGrid.Row): DataGridCells.ILink => {
    const schedule = row.data as IScheduleInfo;
    let displayName = schedule.name;
    if (this.params.displayScheduleDescriptionAsName) {
      displayName = displayName + ' - ' + IScheduleInfo.getScheduleDescription(schedule);
    }
    return {
      text: displayName,
      url: this.params.inServerArea
        ? serverScheduleUrl(ServerUrls.getInstance(), schedule.id, schedule.scheduledAction)
        : siteScheduleUrl(this.params.urls, schedule.id, schedule.scheduledAction),
      iconClassName: 'tb-grid-icon-schedule-stylish',
      iconTitle: Messages.schedule()
    };
  }

  private generateLinkForGroupName = (row: DataGrid.Row): DataGridCells.ILink => {
    const group = row.data as IGroupInfo;
    let displayName = group.name;
    return {
      text: displayName,
      url: this.params.urls.groupUrl(group.id),
      iconClassName: 'tb-grid-icon-group-stylish',
      iconTitle: Messages.group()
    };
  }

  private generateDSCertificationTooltipProps = (row: DataGrid.Row): CertificationWatermark.CertificationTooltipProps => {
    const contentDetail = row.data.contentDetail || row.data;
    const certifier = contentDetail.certificationDetails ? UserMapper.mapOne(contentDetail.certificationDetails.certifier) : null;
    return contentDetail.certificationDetails &&
    {
      id: row.data.id,
      certifier: certifier && {
        customUserStyle: certifier.isHiddenInternalUser ? HiddenUserStyles.HiddenUserStyle as React.CSSProperties : null,
        displayName: certifier.displayName,
        url: certifier.domainName
          ? this.params.urls.userUrl(certifier.username, certifier.domainName)
          : ''
      },
      note: contentDetail.certificationDetails.certificationNote,
      date: MapperHelper.toDate(contentDetail.lastRefreshedAt)
    };
  }

  private generateActionsOptions = (): DataGridCells.ActionCellOptions => {
    return {
      getContentItem: (row: DataGrid.Row) => row.data as IContentInfo,
      isRowSelected: this.isRowSelected,
      isAccessDenied: this.isAccessDenied,
    };
  }

  private showingOnlyDataConnections = (): boolean => {
    return (this.params.contentFilter.length === 1
      && this.params.contentFilter[0] === DataConnectionDefinition.itemType) || isDatasourceConnectionSubSpace(this.params.spaceInfo);
  }

  private siteUserDataGridColumn = (sortableHeaderFormatter: any): DataGrid.Column => {
    return {
      name: CommonColumn.SiteUserDisplayName,
      dataKey: OrderKeys.displayName,
      sortable: true,
      direction: 'asc',
      growToFit: true,
      headerText: Messages.column_fullName(),
      headerFormatter: sortableHeaderFormatter,
      cellFormatter: UserDataGridCells.ProfilePictureCell(
        (row: DataGrid.Row) => {
          const user = row.data as ISiteUserInfo;
          return {
            profileDisplayName: user.displayName,
            imageUrl: user.userImageUrl,
            divShape: ProfilePicture.Shape.Circle
          };
        },
        (row: DataGrid.Row) => {
          const user = row.data as ISiteUserInfo;
          const disabled = user.siteRole === SiteRoles.Guest;
          return {
            text: user.displayName,
            url: disabled ? null : this.params.urls.userUrl(user.username, user.domainName),
            style: disabled ? { color: CommonStyles.Colors.Grey102, textDecoration: 'none', pointerEvents: 'none' } : null
          };
        }
      )
    };
  }
}
